import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, X, AlertCircle, Clock, CheckCircle, TrendingUp, TrendingDown, Loader2, ChevronLeft, ChevronRight, DollarSign, Edit2 } from 'lucide-react';
import { useFuturesTrading, PositionHistoryEntry } from '../hooks/useFuturesTrading';
import { DatabaseFuturesPosition } from '../hooks/useDatabase';
import { PropPositionHistoryEntry } from '../hooks/usePropFirmTrading';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { supabase } from '../lib/supabaseClient';
import { TradingMode } from '../App';
import { useMarketData } from '../contexts/MarketDataContext';
import { calculateDailySwapCost, getSwapConfigForSymbol } from '../constants/swapConfig';
import { formatSwapCostDisplay } from '../utils/swapCalculations';
import TakeProfitStopLossModal from './TakeProfitStopLossModal';
import { Editorial, Typography, BorderRadius } from '../styles/primitives';

interface FuturesMyOrdersProps {
  currentBtcPrice: number;
  futuresPositions: any[];
  onClosePosition: (positionId: string, livePrice: number) => void;
  updateBalances?: (updates: { usdt_balance?: number }) => Promise<void>;
  openOrders?: any[];
  onCancelOrder?: (orderId: string) => Promise<boolean>;
  onCancelAllOrders?: () => Promise<boolean>;
  balances?: { usdt_balance?: number };
  propHistory?: PropPositionHistoryEntry[];
  challengeId?: string;
  selectedPair: string;
  tradingMode: TradingMode;
  onClosePropPosition?: (positionId: string) => Promise<boolean>;
  onCancelPropOrder?: (orderId: string) => Promise<boolean>;
  onCancelAllPropOrders?: () => Promise<boolean>;
  currentSelectedPairPrice: number;
}

interface FuturesOrder {
  id: string;
  user_id: string;
  symbol: string;
  type: 'limit' | 'market' | 'stop';
  side: 'buy' | 'sell';
  price: number | null;
  amount: number;
  leverage: number;
  status: 'open' | 'filled' | 'cancelled';
  created_at: string;
  filled_at: string | null;
  position_id: string | null;
}


const FuturesMyOrders: React.FC<FuturesMyOrdersProps> = ({
  currentBtcPrice,
  futuresPositions,
  onClosePosition,
  updateBalances,
  openOrders = [],
  onCancelOrder,
  onCancelAllOrders,
  balances,
  propHistory = [],
  challengeId,
  selectedPair,
  tradingMode,
  onClosePropPosition,
  onCancelPropOrder,
  onCancelAllPropOrders,
  currentSelectedPairPrice
}) => {
  const { t } = useTranslation();
  const { loadPositionHistory, positionHistory, loading, error } = useFuturesTrading();
  const { marketData, isConnected: realtimeConnected, getPriceBySymbol } = useMarketData();
  const [activeTab, setActiveTab] = useState('positions');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const tabs = [
    { key: 'positions', label: t('futures.positions') },
    { key: 'openOrders', label: t('futures.openOrders') },
    { key: 'positionHistory', label: t('futures.positionHistory') }
  ];
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [processingPositions, setProcessingPositions] = useState<Record<string, boolean>>({});

  // TP/SL Edit Modal State
  const [editTPSLModal, setEditTPSLModal] = useState<{
    isOpen: boolean;
    type: 'takeProfit' | 'stopLoss';
    position: any;
  } | null>(null);

  // Pagination state for Position History
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getLotSize = (symbol: string): number => {
    if (tradingMode === 'cfd') {
      if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('WTI') || symbol.includes('BRENT')) {
        return 100;
      }
      return 100000;
    }
    return 1;
  };

  // Helper function to determine price precision based on symbol
const getPricePrecision = useCallback((symbol: string): number => {
  if (tradingMode === 'cfd') {
    // 🔹 Forex pairs with slash (EUR/USD, AUD/CAD, GBP/JPY, etc.)
    if (symbol.includes('/')) {
      return 5; // Show 5 decimals
    }

    // 🔹 Forex pairs without slash (EURUSD, GBPJPY, etc.)
    if (symbol.length === 6 && !symbol.includes('USDT')) {
      return 5;
    }

    // 🔹 Precious metals & energy
    if (
      symbol.startsWith('XAU') ||
      symbol.startsWith('XAG') ||
      symbol.startsWith('XPT') ||
      symbol.startsWith('XPD') ||
      symbol === 'WTIUSD' ||
      symbol === 'NGUSD'
    ) {
      return 3;
    }

    // 🔹 Industrial metals
    if (symbol.includes('COPPER') || symbol.includes('ALU') || symbol.includes('ZINC')) {
      return 3;
    }

    // 🔹 Stocks
    const stockSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NFLX', 'NVDA', 'BA', 'JPM'];
    if (stockSymbols.includes(symbol)) {
      return 2;
    }
  }

  // 🔹 Default (crypto, etc.)
  return 2;
}, [tradingMode]);


  // Helper function to determine instrument type
  const getInstrumentType = useCallback((symbol: string): string => {
    if (tradingMode === 'cfd') {
      // For CFD mode, look up the instrument in CFD_INSTRUMENTS
      const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
      if (instrument) {
        return instrument.type;
      }
    }
    
    // For non-CFD modes, check if it's crypto
    if (symbol.endsWith('USDT') || symbol.endsWith('BTC') || symbol.endsWith('ETH')) {
      return 'crypto';
    }
    
    // Default fallback
    return 'other';
  }, [tradingMode]);


  // Filter positions based on selectedPair and tradingMode
  const filteredFuturesPositions = useMemo(() => {
    if (challengeId) {
      return futuresPositions; // Show all positions for prop firm challenges
    } else if (tradingMode === 'cfd') {
      // In CFD mode, show all non-crypto positions
      return futuresPositions.filter(position => getInstrumentType(position.symbol) !== 'crypto');
    } else { // tradingMode === 'futures'
      // In Futures mode, show all crypto positions (not just the selected pair)
      return futuresPositions.filter(position => getInstrumentType(position.symbol) === 'crypto');
    }
  }, [futuresPositions, challengeId, tradingMode, selectedPair, getInstrumentType]);

  // Filter open orders based on selectedPair and tradingMode
  const filteredOpenOrders = useMemo(() => {
    if (challengeId) {
      return openOrders; // Show all orders for prop firm challenges
    } else if (tradingMode === 'cfd') {
      // In CFD mode, show all non-crypto orders
      return openOrders.filter(order => getInstrumentType(order.symbol) !== 'crypto');
    } else { // tradingMode === 'futures'
      // In Futures mode, filter by selectedPair and ensure it's a crypto pair
      return openOrders.filter(order => order.symbol === selectedPair && getInstrumentType(order.symbol) === 'crypto');
    }
  }, [openOrders, challengeId, tradingMode, selectedPair, getInstrumentType]);

  // Calculate real-time PnL and ROI for each position
  const positionsWithLiveData = filteredFuturesPositions.map(position => {
    let livePrice = position.currentPrice || position.current_price || 0;
    const symbol = position.symbol;
    
    // Use market data from context - getPriceBySymbol handles symbol conversion
    const realtimePrice = getPriceBySymbol(symbol);
    if (realtimePrice > 0) {
      livePrice = realtimePrice;
    }
    
    const entryPrice = position.entryPrice || position.entry_price || 0;
    const amount = position.amount || 0;
    const leverage = position.leverage || 1;
    const margin = position.margin || 0;
    const side = position.side || 'long';
    const liquidationPrice = position.liquidationPrice || position.liquidation_price || 0;
    const accumulatedSwapCost = position.accumulated_swap_cost || position.accumulatedSwapCost || 0;

    // Calculate daily swap cost for this position
    const positionSize = amount * entryPrice;
    const dailySwapCost = calculateDailySwapCost(symbol, positionSize, leverage);

    // Calculate unrealized PnL based on live price (WITHOUT leverage multiplier)
    let unrealizedPnl = 0;
    if (side === 'long') {
      unrealizedPnl = (livePrice - entryPrice) * amount;
    } else {
      unrealizedPnl = (entryPrice - livePrice) * amount;
    }

    // Subtract accumulated swap costs from PnL
    const netUnrealizedPnl = unrealizedPnl - accumulatedSwapCost;

    // Calculate ROI based on net PnL
    const roi = margin > 0 ? (netUnrealizedPnl / margin) * 100 : 0;
    
    return {
      ...position,
      currentPrice: livePrice,
      entryPrice,
      amount,
      leverage,
      margin,
      side,
      liquidationPrice,
      unrealizedPnl: netUnrealizedPnl,
      grossUnrealizedPnl: unrealizedPnl,
      accumulatedSwapCost,
      dailySwapCost,
      roi,
      takeProfit: position.takeProfit || position.tp_price || null,
      stopLoss: position.stopLoss || position.sl_price || null
    };
  });

  const totalUnrealizedPnl = positionsWithLiveData.reduce((sum, pos) => sum + (pos.unrealizedPnl || 0), 0);
  const totalMargin = positionsWithLiveData.reduce((sum, pos) => sum + (pos.margin || 0), 0);
  const runningPositions = positionsWithLiveData.length;

  // Get the appropriate position history data
  const positionHistoryData = challengeId ? propHistory : positionHistory;
  
  // Calculate pagination for position history
  const totalPages = Math.ceil(positionHistoryData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHistoryItems = positionHistoryData.slice(indexOfFirstItem, indexOfLastItem);
  
  // Reset to page 1 when switching tabs or when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, positionHistoryData.length]);
  
  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Load position history when tab changes to Position History
  useEffect(() => {
    if (activeTab === 'positionHistory') {
      // Only load position history if we're not in a challenge view
      if (!challengeId) {
        loadPositionHistory();
      }
    }
  }, [activeTab, loadPositionHistory, challengeId]);

  // Update error message when error changes
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      
      // Clear error after 5 seconds
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Monitor positions for TP/SL triggers
  useEffect(() => {
    positionsWithLiveData.forEach(position => {
      const { id, currentPrice, side, takeProfit, stopLoss } = position;

      if (!currentPrice || currentPrice <= 0) return;

      let shouldClose = false;
      let reason = '';

      if (takeProfit && takeProfit > 0) {
        if (side === 'long' && currentPrice >= takeProfit) {
          shouldClose = true;
          reason = 'Take Profit triggered';
        } else if (side === 'short' && currentPrice <= takeProfit) {
          shouldClose = true;
          reason = 'Take Profit triggered';
        }
      }

      if (!shouldClose && stopLoss && stopLoss > 0) {
        if (side === 'long' && currentPrice <= stopLoss) {
          shouldClose = true;
          reason = 'Stop Loss triggered';
        } else if (side === 'short' && currentPrice >= stopLoss) {
          shouldClose = true;
          reason = 'Stop Loss triggered';
        }
      }

      if (shouldClose && !processingPositions[id]) {
        handleClosePosition(id).then(() => {
          setSuccessMessage(reason);
        });
      }
    });
  }, [positionsWithLiveData.map(p => `${p.id}-${p.currentPrice}`).join(',')]);

  const handleClosePosition = async (positionId: string) => {
    const position = positionsWithLiveData.find(p => p.id === positionId);
    if (!position) return;

    try {
      setProcessingPositions(prev => ({ ...prev, [positionId]: true }));

      let success = false;
      if (challengeId && onClosePropPosition) {
        success = await onClosePropPosition(positionId);
      } else {
        await onClosePosition(positionId, position.currentPrice);
        success = true;
      }

      if (success) {
        setSuccessMessage('Position closed successfully');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to close position');
    } finally {
      setProcessingPositions(prev => ({ ...prev, [positionId]: false }));
    }
  };

  const closeAllPositions = async () => {
    setIsProcessingAll(true);
    try {
      for (const position of filteredFuturesPositions) {
        await handleClosePosition(position.id);
      }
    } catch (error) {
      console.error('Error closing all positions:', error);
      setErrorMessage('Failed to close all positions');
    } finally {
      setIsProcessingAll(false);
    }
  };

  const handleUpdateTPSL = async (positionId: string, type: 'takeProfit' | 'stopLoss', price: number) => {
    try {
      const tableName = challengeId ? 'prop_positions' : 'futures_positions';
      const column = type === 'takeProfit' ? 'tp_price' : 'sl_price';

      // If price is 0, remove the TP/SL by setting it to null
      const value = price > 0 ? price : null;

      const { error } = await supabase
        .from(tableName)
        .update({ [column]: value })
        .eq('id', positionId);

      if (error) throw error;

      const action = value === null ? 'removed' : 'updated';
      setSuccessMessage(`${type === 'takeProfit' ? 'Take Profit' : 'Stop Loss'} ${action} successfully`);

      if (updateBalances) {
        await updateBalances({});
      }
    } catch (err: any) {
      setErrorMessage(err.message || `Failed to update ${type === 'takeProfit' ? 'Take Profit' : 'Stop Loss'}`);
    }
  };

  // Handle cancel order
  const handleCancelOrder = async (orderId: string) => {
    if (!onCancelOrder && !onCancelPropOrder) return;
    
    try {
      let success = false;
      if (challengeId && onCancelPropOrder) {
        // Use prop firm specific cancel function
        success = await onCancelPropOrder(orderId);
      } else if (onCancelOrder) {
        // Use regular futures cancel function
        success = await onCancelOrder(orderId);
      }
      
      if (success) {
        setSuccessMessage('Order cancelled successfully');
      } else {
        throw new Error('Failed to cancel order');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel order');
    }
  };

  // Handle cancel all orders
  const handleCancelAllOrders = async () => {
    if (!onCancelAllOrders && !onCancelAllPropOrders) return;
    
    setIsProcessingAll(true);
    try {
      let success = false;
      if (challengeId && onCancelAllPropOrders) {
        // Use prop firm specific cancel all function
        success = await onCancelAllPropOrders();
      } else if (onCancelAllOrders) {
        // Use regular futures cancel all function
        success = await onCancelAllOrders();
      }
      
      if (success) {
        setSuccessMessage('All orders cancelled successfully');
      } else {
        throw new Error('Failed to cancel orders');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel orders');
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Format duration from seconds to human-readable format
  const formatDuration = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    if (seconds < 3600) {
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    if (seconds < 86400) {
      return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    }
    return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
  };

  return (
    <div className="rounded-2xl p-3 md:p-8 h-full flex flex-col overflow-hidden" translate="no" style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
    }}>
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 flex items-center gap-2 md:gap-3" style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          borderRadius: '16px',
          padding: '16px'
        }}>
          <AlertCircle size={20} style={{ color: '#dc2626' }} className="flex-shrink-0" />
          <span style={{ color: '#dc2626', fontWeight: 500 }}>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 md:mb-6 flex items-center gap-2 md:gap-3" style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '16px',
          padding: '16px'
        }}>
          <CheckCircle size={20} style={{ color: '#16a34a' }} className="flex-shrink-0" />
          <span style={{ color: '#16a34a', fontWeight: 500 }}>{successMessage}</span>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6 gap-3 overflow-hidden">
        <div className="w-full overflow-hidden">
          <div className="flex gap-2 md:gap-4 overflow-x-auto hide-scrollbar pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="text-xs sm:text-sm font-medium px-2 sm:px-3 md:px-4 py-1.5 md:py-2 transition-all duration-300 whitespace-nowrap flex-shrink-0"
                style={{
                  borderRadius: '12px',
                  background: activeTab === tab.key ? 'rgba(217, 18, 34, 0.1)' : 'transparent',
                  color: activeTab === tab.key ? '#d91222' : 'rgba(0, 0, 0, 0.6)',
                  border: activeTab === tab.key ? '1px solid rgba(217, 18, 34, 0.3)' : '1px solid transparent',
                  boxShadow: activeTab === tab.key ? '0 4px 12px rgba(217, 18, 34, 0.15)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Assets Section */}
        <div className="p-3 md:p-6 w-full md:w-auto" style={{
          background: 'rgba(0, 0, 0, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
        }}>
          <h4 className="font-semibold mb-2 md:mb-4 text-sm md:text-base" style={{ color: '#d91222' }}>{t('futures.assets')}</h4>
          <div className="grid grid-cols-3 md:grid-cols-2 gap-2 md:gap-4">
            <div className="col-span-3 md:col-span-2">
              <span className="text-xs" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.overallBalance')}:</span>
              <div className="font-mono text-base md:text-xl break-all" style={{ color: '#000000', fontWeight: 600 }}>${totalMargin.toFixed(2)} USD</div>
            </div>
            <div>
              <span className="text-xs whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.available')}:</span>
              <div className="font-mono text-xs md:text-sm break-all" style={{ color: '#000000', fontWeight: 600 }}>{balances?.usdt_balance?.toFixed(2) || '0'}</div>
            </div>
            <div>
              <span className="text-xs whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.unrealized')}:</span>
              <div className="font-mono text-xs md:text-sm break-all" style={{
                color: totalUnrealizedPnl >= 0 ? '#16a34a' : '#dc2626',
                fontWeight: 600
              }}>
                {totalUnrealizedPnl.toFixed(2)}
              </div>
            </div>
            <div>
              <span className="text-xs whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.positions')}:</span>
              <div className="font-mono text-xs md:text-sm" style={{ color: '#000000', fontWeight: 600 }}>{runningPositions}</div>
            </div>
            <div className="hidden md:block">
              <span className="text-xs whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.burning')}:</span>
              <div className="font-mono text-xs md:text-sm" style={{ color: '#000000', fontWeight: 600 }}>0</div>
            </div>
            <div>
              <span className="text-xs whitespace-nowrap" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.orders')}:</span>
              <div className="font-mono text-xs md:text-sm" style={{ color: '#000000', fontWeight: 600 }}>{openOrders.length}</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-3 md:mt-4">
            <button
              onClick={closeAllPositions}
              disabled={isProcessingAll || filteredFuturesPositions.length === 0 || loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg md:rounded-xl text-xs sm:text-sm md:text-base font-medium md:font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={14} className="sm:w-4 sm:h-4 animate-spin" />
                  <span className="truncate">{t('futures.processing')}</span>
                </>
              ) : (
                <span className="truncate">{t('futures.closeAllPositions')}</span>
              )}
            </button>
            <button
              onClick={handleCancelAllOrders}
              disabled={isProcessingAll || filteredOpenOrders.length === 0 || loading || (!onCancelAllOrders && !onCancelAllPropOrders)}
              className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg md:rounded-xl text-xs sm:text-sm md:text-base font-medium md:font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 flex items-center justify-center gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={14} className="sm:w-4 sm:h-4 animate-spin" />
                  <span className="truncate">{t('futures.processing')}</span>
                </>
              ) : (
                <span className="truncate">{t('futures.cancelAllOrders')}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'positions' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs mb-4 font-medium" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('futures.size')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.entryPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.currentPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.liquidationPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.margin')}</span>
            <span className="text-xs md:text-sm">Swap Cost</span>
            <span className="text-xs md:text-sm">TP / SL</span>
            <span className="text-xs md:text-sm">{t('futures.pnl')} ({t('futures.roi')})</span>
            <span className="text-xs md:text-sm"></span>
          </div>

          {positionsWithLiveData.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto" translate="no">
              {positionsWithLiveData.map((position, index) => (
                <div key={position.id} className="flex flex-wrap md:flex-nowrap items-center justify-between px-2 sm:px-3 md:px-6 py-3 md:py-4 transition-all duration-300 gap-1 sm:gap-2 md:gap-0" style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.08)';
                }}>
                  <div className="w-1/3 sm:w-1/4 md:w-auto mb-1 md:mb-0">
                    <span className="font-medium text-xs sm:text-sm md:text-base break-all" style={{ color: '#000000' }}>{position.symbol}</span>
                  </div>
                  <div className="w-1/3 sm:w-1/4 md:w-auto mb-1 md:mb-0 text-right md:text-left">
                    <span className={`font-mono text-xs sm:text-sm md:text-base ${position.side === 'long' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(position.amount || 0).toFixed(4)}
                    </span>
                  </div>
                  <div className="w-1/3 sm:w-1/4 md:w-auto mb-1 md:mb-0">
                    <span className={`text-xs sm:text-sm font-bold px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 rounded-lg ${
                      position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                    }`}>
                      {(position.side || 'long').toUpperCase()}
                    </span>
                  </div>
                  <div className="w-1/3 sm:w-1/4 md:w-auto mb-1 md:mb-0 text-right md:text-left">
                    <div className="text-xs md:hidden" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.entryPrice')}</div>
                    <span className="font-mono text-xs sm:text-sm md:text-base" style={{ color: '#000000', fontWeight: 600 }}>{(position.entryPrice || 0).toFixed(getPricePrecision(position.symbol))}</span>
                  </div>
                  <div className="w-1/3 sm:w-1/4 md:w-auto mb-1 md:mb-0">
                    <div className="text-xs md:hidden" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.currentPrice')}</div>
                    <div className="flex items-center gap-1">
                      {realtimeConnected && getPriceBySymbol && getPriceBySymbol(position.symbol) > 0 && (
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                      )}
                      <span className="font-mono text-xs sm:text-sm md:text-base" style={{
                        color: realtimeConnected && getPriceBySymbol && getPriceBySymbol(position.symbol) > 0 ? '#16a34a' : '#000000',
                        fontWeight: 600
                      }}>
                      {(position.currentPrice || 0).toFixed(getPricePrecision(position.symbol))}
                      </span>
                    </div>
                  </div>
                  <div className="hidden lg:block font-mono text-xs sm:text-sm md:text-base" style={{ color: '#dc2626', fontWeight: 600 }}>{(position.liquidationPrice || 0).toFixed(getPricePrecision(position.symbol))}</div>
                  <div className="hidden lg:block font-mono text-xs sm:text-sm md:text-base" style={{ color: '#000000', fontWeight: 600 }}>{(position.margin || 0).toFixed(2)}</div>
                  <div className="hidden lg:block text-right">
                    <div className="text-orange-400 font-mono text-sm">
                      {formatSwapCostDisplay(position.accumulatedSwapCost || 0)}
                    </div>
                  </div>
                  <div className="hidden lg:block text-center">
                    <div className="flex flex-col gap-1">
                      {position.takeProfit ? (
                        <div className="flex items-center gap-1 justify-center">
                          <div className="text-emerald-400 font-mono text-xs">
                            TP: {position.takeProfit.toFixed(getPricePrecision(position.symbol))}
                          </div>
                          <button
                            onClick={() => setEditTPSLModal({ isOpen: true, type: 'takeProfit', position })}
                            className="text-emerald-400/60 hover:text-emerald-400 transition-colors p-0.5"
                          >
                            <Edit2 size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditTPSLModal({ isOpen: true, type: 'takeProfit', position })}
                          className="text-xs transition-colors"
                          style={{ color: 'rgba(0, 0, 0, 0.4)' }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#16a34a'}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.4)'}
                        >
                          + TP
                        </button>
                      )}
                      {position.stopLoss ? (
                        <div className="flex items-center gap-1 justify-center">
                          <div className="text-red-400 font-mono text-xs">
                            SL: {position.stopLoss.toFixed(getPricePrecision(position.symbol))}
                          </div>
                          <button
                            onClick={() => setEditTPSLModal({ isOpen: true, type: 'stopLoss', position })}
                            className="text-red-400/60 hover:text-red-400 transition-colors p-0.5"
                          >
                            <Edit2 size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditTPSLModal({ isOpen: true, type: 'stopLoss', position })}
                          className="text-xs transition-colors"
                          style={{ color: 'rgba(0, 0, 0, 0.4)' }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.4)'}
                        >
                          + SL
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="hidden lg:block text-right">
                    <div className="font-mono text-sm md:text-base" style={{
                      color: (position.unrealizedPnl || 0) >= 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 600
                    }}>
                      {(position.unrealizedPnl || 0).toFixed(2)}
                    </div>
                    <div className="text-xs md:text-sm" style={{
                      color: (position.roi || 0) >= 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 500
                    }}>
                      ({(position.roi || 0).toFixed(2)}%)
                    </div>
                  </div>
                  {/* Mobile-only PnL display */}
                  <div className="w-1/2 sm:w-1/3 md:w-auto mb-1 md:mb-0 lg:hidden">
                    <div className="text-xs" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('futures.pnl')}</div>
                    <div className="font-mono text-xs sm:text-sm" style={{
                      color: (position.unrealizedPnl || 0) >= 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 600
                    }}>
                      {(position.unrealizedPnl || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="w-1/2 sm:w-1/4 md:w-auto mb-1 md:mb-0 text-right">
                    <button
                      onClick={() => handleClosePosition(position.id)}
                      disabled={loading || processingPositions[position.id]}
                      className="transition-colors p-1.5 sm:p-2 rounded-lg disabled:opacity-50"
                      style={{ color: 'rgba(0, 0, 0, 0.6)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#dc2626';
                        e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {processingPositions[position.id] ? (
                        <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-t-slate-400 rounded-full animate-spin" style={{
                          borderColor: 'rgba(0, 0, 0, 0.2)',
                          borderTopColor: 'rgba(0, 0, 0, 0.6)'
                        }} />
                      ) : (
                        <X size={14} className="sm:w-4 sm:h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 flex-1">
              <Package
                size={64}
                className="mb-6"
                style={{
                  color: 'rgba(0, 0, 0, 0.2)',
                  opacity: 0.5,
                }}
              />
              <p
                style={{
                  color: '#000000',
                  fontSize: Typography.BodyLarge,
                  fontWeight: Typography.WeightMedium,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('futures.noPositionsFound')}
              </p>
              <p
                className="mt-2"
                style={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: Typography.BodySmall,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('futures.positionsWillAppear')}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'openOrders' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs text-white/70 mb-4 font-medium">
            <span className="text-xs md:text-sm">{t('common.date')}</span>
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('common.type')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.price')}</span>
            <span className="text-xs md:text-sm">{t('futures.amount')}</span>
            <span className="text-xs md:text-sm">{t('futures.leverage')}</span>
            <span className="text-xs md:text-sm">{t('common.status')}</span>
            <span className="text-xs md:text-sm"></span>
          </div>
          
          {loadingOrders ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredOpenOrders.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto" translate="no">
              {filteredOpenOrders.map((order) => (
                <div key={order.id} className="flex flex-wrap items-center justify-between bg-white px-3 md:px-6 py-3 md:py-4 rounded-xl hover:bg-gray-50 transition-all duration-300 border border-gray-200 hover:border-gray-300 shadow-lg">
                  <div className="w-full md:w-auto mb-2 md:mb-0 flex justify-between items-center">
                    <span className="text-black text-xs md:text-sm font-mono">
                      {new Date(order.createdAt || order.created_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="text-blue-400 text-xs md:hidden">Open</span>
                  </div>

                  <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                    <span className="text-black font-medium text-sm md:text-base">{order.symbol}</span>
                  </div>

                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
                    <span className={`text-xs md:text-sm font-bold px-2 py-1 rounded-lg ${
                      order.side === 'buy' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                    }`}>
                      {order.side.toUpperCase()}
                    </span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-gray-600 md:hidden">{t('common.type')}</div>
                    <span className="text-black text-xs md:text-sm capitalize">{order.type}</span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-gray-600 md:hidden">{t('futures.price')}</div>
                    <span className="text-black font-mono text-xs md:text-sm">{order.price?.toFixed(getPricePrecision(order.symbol)) || 'Market'}</span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-gray-600 md:hidden">{t('futures.amount')}</div>
                    <span className="text-black font-mono text-xs md:text-sm">{(order.amount || 0).toFixed(4)}</span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-gray-600 md:hidden">{t('futures.leverage')}</div>
                    <span className="text-black font-mono text-xs md:text-sm">{order.leverage}x</span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <span className="text-blue-400 text-xs md:text-sm font-bold px-2 py-1 rounded-lg bg-blue-400/10">
                      OPEN
                    </span>
                  </div>

                  <div className="w-1/3 md:w-auto mb-2 md:mb-0 text-right">
                    <button
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={loading}
                      className="text-gray-600 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg disabled:opacity-50"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 flex-1">
              <Clock
                size={64}
                className="mb-6"
                style={{
                  color: 'black',
                  opacity: 0.5,
                }}
              />
              <p
                style={{
                  color: '#000000',
                  fontSize: Typography.BodyLarge,
                  fontWeight: Typography.WeightMedium,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('No Open Orders')}
              </p>
              <p
                className="mt-2"
                style={{
                  color: 'black',
                  fontSize: Typography.BodySmall,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('futures.ordersWillAppear')}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'positionHistory' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs text-white/70 mb-4 font-medium">
            <span className="text-xs md:text-sm">{t('common.date')}</span>
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.entryPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.exitPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.amount')}</span>
            <span className="text-xs md:text-sm">{t('futures.duration')}</span>
            <span className="text-xs md:text-sm">Swap Cost</span>
            <span className="text-xs md:text-sm">{t('futures.pnl')}</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : currentHistoryItems.length > 0 ? (
            <>
              <div className="space-y-3 flex-1 overflow-auto" translate="no">
              {currentHistoryItems.map((position) => (
  <div key={position.id} className="flex flex-wrap items-center justify-between bg-white px-3 md:px-6 py-3 md:py-4 rounded-xl hover:bg-gray-50 transition-all duration-300 border border-gray-200 hover:border-gray-300 shadow-lg">
    
    {/* Date */}
    <div className="w-full md:w-auto mb-2 md:mb-0 flex justify-between items-center">
      <span className="text-black text-xs md:text-sm font-mono">
        {position.closeTime
          ? new Date(position.closeTime).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : 'N/A'}
      </span>
      <span
        className={`text-xs md:hidden font-bold px-2 py-1 rounded-lg ${
          position.pnl >= 0
            ? 'text-emerald-400 bg-emerald-400/10'
            : 'text-red-400 bg-red-400/10'
        }`}
      >
        {position.pnl >= 0 ? 'PROFIT' : 'LOSS'}
      </span>
    </div>

    {/* Symbol */}
    <div className="w-1/2 md:w-auto mb-2 md:mb-0">
      <span className="text-black font-medium text-sm md:text-base">
        {position.symbol}
      </span>
    </div>

    {/* Side */}
    <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
      <span
        className={`text-xs md:text-sm font-bold px-2 py-1 rounded-lg ${
          position.side === 'long'
            ? 'text-emerald-400 bg-emerald-400/10'
            : 'text-red-400 bg-red-400/10'
        }`}
      >
        {position.side.toUpperCase()}
      </span>
    </div>

    {/* Entry / Exit */}
    <div className="w-1/3 md:w-auto mb-2 md:mb-0">
      <span className="text-black font-mono text-xs md:text-sm">
        {position.entryPrice.toFixed(getPricePrecision(position.symbol))}
      </span>
    </div>
    <div className="w-1/3 md:w-auto mb-2 md:mb-0">
      <span className="text-black font-mono text-xs md:text-sm">
        {position.exitPrice.toFixed(getPricePrecision(position.symbol))}
      </span>
    </div>

    {/* Amount */}
    <div className="w-1/3 md:w-auto mb-2 md:mb-0">
      <span className="text-black font-mono text-xs md:text-sm">
        {position.amount.toFixed(4)}
      </span>
    </div>

    {/* Duration */}
    <div className="w-1/2 md:w-auto mb-2 md:mb-0">
      <span className="text-black text-xs md:text-sm">
        {position.durationSeconds
          ? formatDuration(position.durationSeconds)
          : 'N/A'}
      </span>
    </div>

    {/* Swap Cost */}
    <div className="hidden lg:block text-right">
      <div className="text-orange-400 font-mono text-xs">
        {formatSwapCostDisplay(position.accumulatedSwapCost || 0)}
      </div>
      {position.totalSwapDays && position.totalSwapDays > 0 && (
        <div className="text-black text-xs">
          {position.totalSwapDays} {position.totalSwapDays === 1 ? 'day' : 'days'}
        </div>
      )}
    </div>

    {/* PnL */}
    <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right">
      <div className="flex items-center gap-1 justify-end md:justify-start">
        {position.pnl >= 0 ? (
          <TrendingUp size={14} className="text-emerald-400" />
        ) : (
          <TrendingDown size={14} className="text-red-400" />
        )}
        <span
          className={`font-mono text-xs md:text-sm ${
            position.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}
        >
          {position.pnl.toFixed(2)}
        </span>
      </div>
    </div>
  </div>
))}




              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                  <div className="text-sm text-white/70">
                    {t('common.showing')} {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, positionHistoryData.length)} {t('common.of')} {positionHistoryData.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg bg-slate-800/50 text-white/70 hover:text-white hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-sm text-white px-3">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg bg-slate-800/50 text-white/70 hover:text-white hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 flex-1">
              <Package
                size={64}
                className="mb-6"
                style={{
                  color: '#000000',
                  opacity: 0.5,
                }}
              />
              <p
                style={{
                  color: '#000000',
                  fontSize: Typography.BodyLarge,
                  fontWeight: Typography.WeightMedium,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('futures.noPositionHistory')}
              </p>
              <p
                className="mt-2"
                style={{
                  color: '#000000',
                  fontSize: Typography.BodySmall,
                  lineHeight: Typography.LeadingNormal,
                }}
              >
                {t('futures.historyWillAppear')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* TP/SL Edit Modal */}
      {editTPSLModal && (
        <TakeProfitStopLossModal
          isOpen={editTPSLModal.isOpen}
          onClose={() => setEditTPSLModal(null)}
          type={editTPSLModal.type}
          side={editTPSLModal.position.side}
          entryPrice={editTPSLModal.position.entryPrice}
          amount={editTPSLModal.position.amount / getLotSize(editTPSLModal.position.symbol)}
          leverage={editTPSLModal.position.leverage}
          lotSize={getLotSize(editTPSLModal.position.symbol)}
          variant="horizontal"
          onConfirm={(price) => {
            handleUpdateTPSL(editTPSLModal.position.id, editTPSLModal.type, price);
            setEditTPSLModal(null);
          }}
        />
      )}
    </div>
  );
};

export default FuturesMyOrders;